import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Footer from 'src/presentation/components/organisms/Footer';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import ImageMap from 'src/presentation/components/molecules/ImageMap';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { navigatePage } from 'src/presentation/helpers/NavigationHelper';
import Translations from 'src/core/utils/Translations';
import type Constants from 'src/core/utils/Constants';
import type { MapHotspot } from 'src/domain/stores/useMapStore';

interface MapData {
	hotspots: MapHotspot[];
	floorplan_color: string;
	floorplan_image: string;
	hotspot_style: string;
}

const MapLayout = () => {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenFloorplan>>();
	const [interactionsComplete, setInteractionsComplete] = useState(false);

	const eventStore = useEventStore();

	const { page, isLoading } = usePageFromRoute(route?.params, { type: 'Floorplan' });
	const mapData = page ? (page.data as MapData) : null;
	const title = page?.name ?? '';

	// Without an uploaded floorplan image there is nothing for ImageMap to load (it would otherwise sit on
	// "Kaart laden..." forever), so fall back to the same empty-state placeholder the other screen types show.
	const hasFloorplan = !!mapData?.floorplan_image;

	// Defer mounting the (heavy) ImageMap until the push animation has settled so the transition stays smooth;
	// the framed loader below fills the screen in the meantime.
	useEffect(() => {
		const interactionTask = afterInteractions(() => {
			setInteractionsComplete(true);
		});

		return () => {
			interactionTask.cancel?.();
		};
	}, []);

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>

			{isLoading && !page ? (
				<Loader />
			) : !hasFloorplan ? (
				<EmptyState icon="map-outline" message={Translations.textNoMapMessage} title={Translations.textNoMapTitle} />
			) : interactionsComplete ? (
				<ImageMap
					data={mapData}
					onLocationDetailClick={(data) => {
						if (data.type) navigatePage(navigation, data.type, data);
					}}
				/>
			) : (
				<Loader />
			)}

			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
};

export default MapLayout;
